<script setup>
import { ref, onMounted, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import ImageUpload from '@/components/ImageUpload.vue';

const { t } = useI18n({ useScope: 'global' });
const emit = defineEmits(['getCutData']);

const cutData = ref({
  index: null,
  width: null,
  height: 0,
  imageId: null,
  path: null,
  note: ''
});

const closeButton = ref(null);
const filmCutsWidth = ['1.83', '1.52', '1.22', '0.91', '0.76', '0.61', '0.3'];

function normalizePath(raw) {
  if (!raw || raw === 'null' || raw === 'undefined') return '';
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  const cleaned = raw.replace(/\\/g, '/').replace(/[^\x20-\x7E]/g, '');
  return decodeURIComponent(cleaned.startsWith('/') ? cleaned : '/' + cleaned);
}

onMounted(() => {
  const editCutModal = document.getElementById('editCutModal');
  if (!editCutModal) return;

  editCutModal.addEventListener('show.bs.modal', function (event) {
    const button = event.relatedTarget;
    if (!button) return;

    if (button.id === 'addFilmCutsItem') {
      cutData.value.index = null;
    }

    const rawImageId = button.getAttribute('data-image-id');
    const rawPath = button.getAttribute('data-path');

    cutData.value = {
      index: button.getAttribute('data-index') ?? null,
      width: parseFloat(button.getAttribute('data-width') ?? 0),
      height: parseFloat(button.getAttribute('data-height') ?? 0),
      note: button.getAttribute('data-note') ?? '',
      imageId: (rawImageId && rawImageId !== 'null') ? rawImageId : null,
      path: normalizePath(rawPath)
    };
  });

  editCutModal.addEventListener('hide.bs.modal', function () {
    const active = document.activeElement;
    if (active instanceof HTMLElement && editCutModal.contains(active)) {
      active.blur();
    }
  });

  editCutModal.addEventListener('hidden.bs.modal', function () {
    if (cutData.value.index !== null) {
      emit('getCutData', cutData.value);
    }
  });
});

function getCutData() {
  emit('getCutData', cutData.value);
  closeModal();
  return cutData.value;
}

function closeModal() {
  closeButton.value?.click();
}

function formatFilmCutsHeight(input) {
  let formattedInput = input.replace(/,/g, '.').replace(/[^0-9.]/g, '');
  const firstDotIndex = formattedInput.indexOf('.');
  if (firstDotIndex !== -1) {
    formattedInput = formattedInput.substring(0, firstDotIndex + 1) + formattedInput.substring(firstDotIndex + 1).replace(/\./g, '');
  }
  cutData.value.height = formattedInput;
}

function getImageData(data) {
  cutData.value.imageId = data._id;
  cutData.value.path = normalizePath(data.path);
}
</script>

<template>
  <div class="col-md-4 align-items-center justify-content-center d-flex">
    <div id="addFilmCutsItem" data-bs-toggle="modal" data-bs-target="#editCutModal"
         class="w-100 d-flex justify-content-center align-items-center">
      <span>+</span>
    </div>

    <div class="modal fade" id="editCutModal" tabindex="-1"
         data-bs-backdrop="static"
         data-bs-keyboard="false"
         aria-labelledby="editCutModalLabel"
         aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editCutModalLabel">
              <template v-if="cutData.index !== null">
                {{ t('labels.editCut') }} {{ Number(cutData.index) + 1 }}
              </template>
              <template v-else>
                {{ t('labels.addCutToOrder') }}
              </template>
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" ref="closeButton" aria-label="Close" />
          </div>

          <div class="modal-body">
            <div class="input-group mb-3">
              <small class="d-block w-100 mb-2">{{ t('labels.filmWidth') }}</small>
              <select class="form-select" v-model="cutData.width">
                <option v-for="(element, index) in filmCutsWidth" :key="index" :value="element">{{ element }}</option>
              </select>
            </div>

            <div class="input-group mb-3">
              <span class="input-group-text">{{ t('labels.filmLength') }}</span>
              <input type="text" autocomplete="off" class="form-control"
                     @input="formatFilmCutsHeight($event.target.value)"
                     v-model="cutData.height">
            </div>

            <div class="input-group">
              <span class="input-group-text">{{ t('tableheadings.note') }}</span>
              <textarea class="form-control" v-model="cutData.note"></textarea>
            </div>

            <div v-if="cutData.path && !cutData.imageId" class="position-relative w-100 mt-3 border rounded p-2 text-center" style="background: #fff;">
              <button type="button" @click="cutData.path = null" class="btn btn-sm btn-outline-danger position-absolute top-0 end-0 m-1" style="z-index: 10; padding: 1px 6px;">
                X
              </button>

              <a :href="cutData.path" data-fancybox="cutImageZoom" class="d-flex w-100 justify-content-center align-items-center" style="cursor: zoom-in; min-height: 140px;">
                <img :src="cutData.path" alt="Лекало" style="max-height: 180px; max-width: 100%; object-fit: contain; mix-blend-mode: multiply;">
              </a>
            </div>

            <div class="input-group mt-3" v-show="!cutData.path || cutData.imageId">
              <ImageUpload :maxSize="5"
                           :key="cutData.imageId"
                           :image-id="cutData.imageId"
                           @file-uploaded="getImageData"
                           accept="jpg,png,webp,gif,jpeg" />
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" @click="getCutData" class="btn btn-primary w-100">
              <template v-if="cutData.index !== null">
                {{ t('buttons.apply') }}
              </template>
              <template v-else>
                {{ t('buttons.add') }}
              </template>
            </button>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
#addFilmCutsItem {
  background: var(--bs-tertiary-color);
  height: 130px;
  text-align: center;
  color: white;
  cursor: pointer;
  opacity: .55;

  span {
    font-size: 50px;
  }
}
</style>